<template>
    <Modal id="table-picker-modal"
           data-component="TablePicker"
           title="TABLE_PICKER_MODAL.MODAL_TITLE"
           icon="fa-table-picnic"
           cancel-label="CLOSE"
           :cancel="canceled">
        <p v-if="alertMessage"
           class="alert alert-info">
            <i class="fa-solid fa-circle-exclamation"></i> {{ l(alertMessage) }}
        </p>

        <div v-if="sortedTables.length > 0"
             class="table-picker__tables-grid">
            <button v-for="table in sortedTables"
                    :key="table.navOrder"
                    class="table-picker__btn"
                    :class="{ 'active': table.status === 'Active' }"
                    @click="setReference(table.name)">
                <span class="table-name">{{ table.name }}</span>
            </button>
        </div>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { updateAngularUi } from '@/functions/angular';

export default {
    components: {
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * If true, the modal will just return the selected table name
         * without attempting to set ref on the current cart.
         */
        resolveOnly: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            /**
             *
             * @type {?TablePlan}
             */
            tablePlan: null,
        };
    },

    computed: {
        /**
         * Returns a filtered and sorted list of tables from the TablePlan.
         * Filters out inactive tables and sorts by navOrder.
         * * @returns {Array<Table>}
         */
        sortedTables() {
            if (!this.tablePlan?.tables) {
                return [];
            }
            return this.tablePlan.tables
                .filter((table) => table.status === 'Active')
                .sort((a, b) => a.navOrder - b.navOrder);
        },

        alertMessage() {
            if (this.sortedTables.length === 0) {
                return 'TABLE_PICKER_MODAL.NO_TABLES_AVAILABLE';
            }
            return null;
        },
    },

    mounted() {
        this.loadTablePlan();
    },

    methods: {
        loadTablePlan() {
            try {
                const device = container.currentDevice.getDevice();
                const tablePlan = device?.deviceGroup?.tablePlan || null;
                if (tablePlan) {
                    this.tablePlan = tablePlan;
                }
            } catch (e) {
                console.error('TablePicker: Failed to load table plan', e);
            }
        },

        setReference(ref) {
            if (this.resolveOnly) {
                this.resolve({
                    value: ref,
                });
                return;
            }

            container.staffRefValidator
                .setCartStaffRef(container.cartService.currentCart, ref)
                .then(() => {
                    this.resolve({
                        refSet: true,
                    });
                }).finally(() => {
                    updateAngularUi();
                });
        },

        canceled() {
            container.vueModalFactory.closeAllOfType(MODAL_NAMES__EPOS.TABLE_PICKER);
        },
    },
};

</script>

<style lang="less">
@import (reference) "../../../../less/variables";

.table-picker__tables-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: @gutter-width;
    @media (max-width: @screen-sm-max) {
        grid-template-columns: repeat(2, 1fr);
    }
}

.table-picker__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 80px;
    background-color: @white;
    border: 1px solid @btn-default-border;
    border-radius: @border-radius-base;
    font-size: @font-size-base;
    cursor: pointer;
    transition: all 0.2s;
}

.table-picker__btn:hover {
    background-color: @btn-default-bg;
}
</style>
